import type { ComponentPropsWithoutRef, CSSProperties, Ref } from 'react';

export type StatusVariant = 'danger' | 'attention' | 'info' | 'capability' | 'muted';
export type StatusTone = 'solid' | 'soft';
export type StatusShape = 'square' | 'circle';

type BaseProps = ComponentPropsWithoutRef<'span'> & {
  /** `square` (default) hugs the content; `circle` produces a fixed-width round badge for single-glyph indicators. */
  shape?: StatusShape;
  ref?: Ref<HTMLSpanElement>;
};

type VariantProps = BaseProps & {
  variant: StatusVariant;
  /** Visual loudness. `solid` (default) is the saturated alert treatment; `soft` is the tinted label treatment. */
  tone?: StatusTone;
  color?: never;
};

type CustomColorProps = BaseProps & {
  /** Override the background with an arbitrary color (user-defined label color). Text is forced to white. `tone` does not apply. */
  color: string;
  variant?: never;
  tone?: never;
};

type StatusCellProps = VariantProps | CustomColorProps;

// Loose internal shape — the union typed parameter enforces correctness at
// call sites; this is only used to destructure both branches in one go.
type InternalProps = BaseProps & {
  variant?: StatusVariant;
  tone?: StatusTone;
  color?: string;
};

const SOLID: Record<StatusVariant, string> = {
  danger: 'bg-status-danger text-white',
  attention: 'bg-status-attention text-white',
  info: 'bg-status-info text-white',
  capability: 'bg-status-capability text-white',
  muted: 'bg-status-muted text-white',
};

const SOFT: Record<StatusVariant, string> = {
  danger: 'bg-status-danger-soft text-status-danger',
  attention: 'bg-status-attention-soft text-status-attention',
  info: 'bg-status-info-soft text-status-info',
  capability: 'bg-status-capability-soft text-status-capability',
  muted: 'bg-status-muted-soft text-status-muted',
};

/**
 * Status indicator pill. Single source of truth for the visual chrome shared by
 * negative QOH, suggested item code, promo, disabled badges, AND user-defined
 * product labels. Bold + saturated + padded so it reads on cheap LCDs under
 * store fluorescents.
 *
 * Use `variant` for token-palette statuses (danger/attention/info/capability/
 * muted) and `color` for user-chosen label colors. The chrome (radius, padding, font) is
 * identical either way, so labels and statuses cannot drift apart.
 *
 * Compose with Tooltip via Radix `asChild` for the contextual gloss.
 */
export function StatusCell(props: StatusCellProps) {
  // Destructure as intersection so `rest` doesn't carry the discriminating
  // props into the DOM. The union-typed parameter still enforces correct usage
  // at every call site.
  const {
    shape = 'square',
    className,
    children,
    style,
    variant,
    tone,
    color,
    ...rest
  } = props as InternalProps;

  // The 28px table row (#6) gives a 20px cell content area (the cell wrapper is
  // py-1). A `text-xs` pill is 16px tall; `py-0.5` lifts it to the full 20px the
  // brief's "generous padding" calls for, and circle badges match at h-5/w-5.
  const sizing = shape === 'circle' ? 'h-5 w-5 justify-center' : 'px-1.5 py-0.5';

  let palette: string;
  let inlineStyle: CSSProperties | undefined = style;
  if (variant) {
    palette = (tone ?? 'solid') === 'solid' ? SOLID[variant] : SOFT[variant];
  } else {
    palette = 'text-white';
    inlineStyle = { ...style, backgroundColor: color };
  }

  return (
    <span
      {...rest}
      style={inlineStyle}
      className={`inline-flex items-center rounded text-xs font-bold ${sizing} ${palette}${className ? ` ${className}` : ''}`}
    >
      {children}
    </span>
  );
}
